import { useState, useCallback, useEffect } from 'react';
import { getCustomer } from 'api/customer';
import { Customer } from 'containers/pages/customers/types';

interface UseCustomerDetailReturn {
  open: boolean;
  customer: Customer | null;
  loading: boolean;
  error: string | null;
  activeTab: number;
  openPanel: (customerId: number) => void;
  closePanel: () => void;
  setActiveTab: (tab: number) => void;
  refetch: () => void;
}

export default function useCustomerDetail(): UseCustomerDetailReturn {
  const [open, setOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchCustomer = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCustomer(id);
      setCustomer(data);
    } catch (err) {
      setError('Failed to load customer details. Please try again.');
      console.error('[useCustomerDetail] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && selectedCustomerId !== null) {
      fetchCustomer(selectedCustomerId);
    }
  }, [open, selectedCustomerId, fetchCustomer]);

  const openPanel = useCallback((customerId: number) => {
    setSelectedCustomerId(customerId);
    setActiveTab(0);
    setCustomer(null);
    setError(null);
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
  }, []);

  const refetch = useCallback(() => {
    if (selectedCustomerId !== null) {
      fetchCustomer(selectedCustomerId);
    }
  }, [selectedCustomerId, fetchCustomer]);

  return {
    open,
    customer,
    loading,
    error,
    activeTab,
    openPanel,
    closePanel,
    setActiveTab,
    refetch,
  };
}
